import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheck, FiX, FiRotateCw, FiClock } from 'react-icons/fi';
import './Flashcard.css';

const Flashcard = ({ card, isFlipped, setIsFlipped, onAnswer, flashcards }) => {
  const [timeLeft, setTimeLeft] = useState(7);
  const [timerActive, setTimerActive] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showResult, setShowResult] = useState(false);

  const [options, setOptions] = useState([]);

  useEffect(() => {
    // Generate multiple choice options
    const generateOptions = () => {
      if (!flashcards || flashcards.length < 4) {
        return [card.answer];
      }

      const options = [card.answer];
      const otherCards = flashcards.filter(c => c.id !== card.id);
      
      // Add 3 random wrong answers
      while (options.length < 4 && otherCards.length > 0) {
        const randomCard = otherCards[Math.floor(Math.random() * otherCards.length)];
        if (!options.includes(randomCard.answer)) {
          options.push(randomCard.answer);
        }
        otherCards.splice(otherCards.indexOf(randomCard), 1);
      }
      
      // Shuffle options
      return options.sort(() => Math.random() - 0.5);
    };

    if (isFlipped && flashcards) {
      setOptions(generateOptions());
      setTimeLeft(7);
      setTimerActive(true);
      setSelectedOption(null);
      setShowResult(false);
    } else {
      setTimerActive(false);
    }
  }, [isFlipped, card.id, card.answer, flashcards]);

  useEffect(() => {
    let interval;
    if (timerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && timerActive) {
      // Time's up - mark as incorrect
      setTimerActive(false);
      setShowResult(true);
      setTimeout(() => {
        onAnswer(false);
      }, 1500);
    }
    return () => clearInterval(interval);
  }, [timerActive, timeLeft, onAnswer]);

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleOptionSelect = (selectedAnswer) => {
    if (selectedOption || !timerActive) return;
    
    setSelectedOption(selectedAnswer);
    setTimerActive(false);
    setShowResult(true);
    
    const isCorrect = selectedAnswer === card.answer;
    setTimeout(() => {
      onAnswer(isCorrect);
    }, 1500);
  };

  const getOptionClass = (option) => {
    if (!showResult) return 'option-btn';
    
    if (option === card.answer) {
      return 'option-btn correct-answer';
    } else if (option === selectedOption) {
      return 'option-btn wrong-answer';
    } else {
      return 'option-btn disabled';
    }
  };

  const getTimerColor = () => {
    if (timeLeft > 4) return '#4CAF50';
    if (timeLeft > 2) return '#FF9800';
    return '#F44336';
  };

  return (
    <div className="flashcard-container">
      <motion.div
        className="flashcard-wrapper"
        onClick={!isFlipped ? handleFlip : undefined}
        whileHover={!isFlipped ? { scale: 1.02 } : {}}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
      >
        <AnimatePresence mode="wait">
          {!isFlipped ? (
            <motion.div
              key="front"
              className="flashcard-face flashcard-front"
              initial={{ rotateY: 0 }}
              animate={{ rotateY: 0 }}
              exit={{ rotateY: -90 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <div className="card-content">
                <div className="card-branding">whatsthis.today</div>
                <div className="card-label">Question</div>
                {card.image && (
                  <div className="card-image">
                    <img src={card.image} alt="Question" />
                  </div>
                )}
                <h2 className="card-text">{card.question}</h2>
                <div className="flip-hint">
                  <FiRotateCw />
                  <span>Tap to reveal answer</span>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="back"
              className="flashcard-face flashcard-back"
              initial={{ rotateY: 90 }}
              animate={{ rotateY: 0 }}
              exit={{ rotateY: 90 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <div className="card-content">
                <div className="card-branding">whatsthis.today</div>
                <div className="card-header">
                  <div className="card-label">Choose the correct answer</div>
                  {timerActive && (
                    <div className="timer-display">
                      <FiClock />
                      <span 
                        className="timer-count"
                        style={{ color: getTimerColor() }}
                      >
                        {timeLeft}s
                      </span>
                    </div>
                  )}
                </div>
                
                {showResult && (
                  <div className="result-message">
                    {selectedOption === card.answer ? (
                      <div className="correct-message">
                        <FiCheck /> Correct!
                      </div>
                    ) : (
                      <div className="incorrect-message">
                        <FiX /> {timeLeft === 0 ? "Time's up!" : "Incorrect!"}
                      </div>
                    )}
                  </div>
                )}

                <div className="multiple-choice-options">
                  {options.map((option, index) => (
                    <motion.button
                      key={index}
                      className={getOptionClass(option)}
                      onClick={() => handleOptionSelect(option)}
                      disabled={!timerActive || showResult}
                      whileHover={timerActive && !showResult ? { scale: 1.02 } : {}}
                      whileTap={timerActive && !showResult ? { scale: 0.98 } : {}}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <span className="option-letter">{String.fromCharCode(65 + index)}</span>
                      <span className="option-text">{option}</span>
                      {showResult && option === card.answer && <FiCheck className="option-icon" />}
                      {showResult && option === selectedOption && option !== card.answer && <FiX className="option-icon" />}
                    </motion.button>
                  ))}
                </div>

                {timerActive && !showResult && (
                  <div className="timer-bar">
                    <motion.div 
                      className="timer-progress"
                      initial={{ width: '100%' }}
                      animate={{ width: '0%' }}
                      transition={{ duration: 7, ease: 'linear' }}
                      style={{ backgroundColor: getTimerColor() }}
                    />
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default Flashcard; 